import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { patientsApi } from '../../api/patients';
import { PatientForm } from '../../components/patients/PatientForm';
import { toast } from '../../components/ui/Toast';
import type { Patient, PatientFormData, CreatePatientRequest, UpdatePatientRequest } from '../../types/patient';

export function PatientFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;

  const [patient, setPatient] = useState<Patient | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) return;
    const loadPatient = async () => {
      setIsLoading(true);
      try {
        const data = await patientsApi.getById(id);
        setPatient(data);
      } catch (err: any) {
        setError(err.message || 'Error al cargar paciente');
      } finally {
        setIsLoading(false);
      }
    };
    loadPatient();
  }, [id, isEditing]);

  const handleSubmit = async (formData: PatientFormData) => {
    setIsSaving(true);
    try {
      const cleanData = Object.fromEntries(Object.entries(formData).filter(([_, v]) => v !== ''));
      if (isEditing && id) {
        await patientsApi.update(id, cleanData as UpdatePatientRequest);
        toast.success('Paciente actualizado', 'Los cambios se han guardado correctamente.');
        navigate(`/pacientes/${id}`, { replace: true });
      } else {
        const newPatient = await patientsApi.create(cleanData as CreatePatientRequest);
        toast.success('Paciente creado', 'El paciente se ha registrado correctamente.');
        navigate(`/pacientes/${newPatient.id}`, { replace: true });
      }
    } catch (err: any) {
      toast.error('Error al guardar', err.message || 'No se pudo guardar el paciente');
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isEditing && id) navigate(`/pacientes/${id}`);
    else navigate('/pacientes');
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/pacientes')} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="h-5 w-5 text-gray-600" /></button>
          <div className="animate-pulse"><div className="h-8 w-48 bg-gray-200 rounded" /></div>
        </div>
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => <div key={i} className="bg-white rounded-lg shadow p-6"><div className="h-6 w-32 bg-gray-200 rounded mb-4" /><div className="grid grid-cols-2 gap-4"><div className="h-10 bg-gray-100 rounded" /><div className="h-10 bg-gray-100 rounded" /></div></div>)}
        </div>
      </div>
    );
  }

  if (isEditing && error && !patient) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/pacientes')} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="h-5 w-5 text-gray-600" /></button>
          <h1 className="text-2xl font-bold text-gray-900">Error</h1>
        </div>
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft className="h-5 w-5 text-gray-600" /></button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Editar paciente' : 'Nuevo paciente'}</h1>
          {isEditing && patient && <p className="text-sm text-gray-500 mt-1">{patient.fullName} - {patient.documentNumber}</p>}
        </div>
      </div>
      <PatientForm initialData={patient || undefined} onSubmit={handleSubmit} onCancel={handleCancel} isLoading={isSaving} />
    </div>
  );
}
